import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getNews, getNewsSentiment, getTrendingStocks } from '../../api/client';

const safeHref = (url) => (url && /^https?:\/\//i.test(url)) ? url : '#';

// ── Meta helpers ──────────────────────────────────────────────────────────────
const COMPOSITE_META = {
  strong_bullish: { label: '🚀 Strong Bullish', short: '🚀 Strong Bull', color: '#16a34a', bg: '#16a34a18', border: '#16a34a44' },
  bullish:        { label: '📈 Bullish',         short: '📈 Bullish',     color: 'var(--green)', bg: '#16a34a11', border: '#16a34a33' },
  neutral:        { label: '➡️ Neutral',          short: '➡️ Neutral',    color: 'var(--muted)', bg: 'var(--bg)', border: 'var(--border)' },
  bearish:        { label: '📉 Bearish',          short: '📉 Bearish',    color: 'var(--red)',   bg: '#dc262611', border: '#dc262633' },
  strong_bearish: { label: '💣 Strong Bearish',   short: '💣 Strong Bear',color: '#b91c1c',     bg: '#dc262618', border: '#dc262644' },
};

const ARTICLE_SENTIMENT_META = {
  bullish: { color: 'var(--green)', bg: '#16a34a11', border: '#16a34a33', label: '▲ Bullish' },
  bearish: { color: 'var(--red)',   bg: '#dc262611', border: '#dc262633', label: '▼ Bearish' },
  neutral: { color: 'var(--muted)', bg: 'var(--bg)', border: 'var(--border)', label: '→ Neutral' },
};

const FACTOR_ICONS = { news: '📰', momentum: '⚡', rsi: '📊', volume: '📦', ma_cross: '〽️' };
const FACTOR_NAMES = { news: 'News', momentum: 'Momentum', rsi: 'RSI-14', volume: 'Volume', ma_cross: 'MA-20' };

// ── Confidence dots ───────────────────────────────────────────────────────────
function ConfidenceDots({ score, maxScore = 5, color }) {
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      {Array.from({ length: maxScore }).map((_, i) => (
        <span key={i} style={{
          display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
          background: i < score ? color : 'var(--border)',
          transition: 'background 0.2s',
        }} />
      ))}
    </div>
  );
}

// ── Factor row inside stock card ──────────────────────────────────────────────
function FactorRow({ name, factor }) {
  const voteColor = factor.vote > 0 ? 'var(--green)' : factor.vote < 0 ? 'var(--red)' : 'var(--muted)';
  const voteIcon  = factor.vote > 0 ? '▲' : factor.vote < 0 ? '▼' : '—';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
        {FACTOR_ICONS[name]} {FACTOR_NAMES[name]}
      </span>
      <span style={{ fontSize: 11, color: voteColor, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
        <span>{voteIcon}</span>
        <span style={{ color: 'var(--muted)', fontWeight: 400, maxWidth: 160, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {factor.label}
        </span>
      </span>
    </div>
  );
}

// ── Stock sentiment card ──────────────────────────────────────────────────────
function StockSentimentCard({ stock, onClick, active }) {
  const [expanded, setExpanded] = useState(false);
  const meta    = COMPOSITE_META[stock.composite_label] || COMPOSITE_META.neutral;
  const barPct  = Math.min(100, stock.confidence_score * 20);

  return (
    <div
      style={{
        borderRadius: 8, marginBottom: 8, overflow: 'hidden',
        border: `1px solid ${active ? meta.color : 'var(--border)'}`,
        background: active ? meta.bg : 'var(--surface)',
        transition: 'all 0.15s',
      }}
    >
      {/* Header row */}
      <div
        style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
        onClick={() => onClick(stock.symbol)}
      >
        {/* Symbol + name */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: active ? meta.color : 'var(--text)' }}>
              {stock.symbol}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
              background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
              whiteSpace: 'nowrap',
            }}>
              {meta.short}
            </span>
          </div>
          {/* Confidence bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1, height: 4, background: 'var(--bg)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${barPct}%`, background: meta.color, borderRadius: 99 }} />
            </div>
            <ConfidenceDots score={stock.confidence_score} color={meta.color} />
            <span style={{ fontSize: 10, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{stock.confidence}</span>
          </div>
        </div>

        {/* Vote summary */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, display: 'flex', gap: 8 }}>
            <span style={{ color: 'var(--green)', fontWeight: 700 }}>▲{stock.composite_bull_votes}</span>
            <span style={{ color: 'var(--red)',   fontWeight: 700 }}>▼{stock.composite_bear_votes}</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
            {stock.mentions} articles
          </div>
        </div>

        {/* Expand toggle */}
        <span
          style={{ fontSize: 11, color: 'var(--muted)', cursor: 'pointer', padding: '0 4px', flexShrink: 0 }}
          onClick={e => { e.stopPropagation(); setExpanded(x => !x); }}
        >
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      {/* Expanded factor breakdown */}
      {expanded && (
        <div style={{ padding: '0 12px 12px', borderTop: '1px solid var(--border)' }}>
          <div style={{ paddingTop: 10 }}>
            {stock.factors && Object.entries(stock.factors).map(([name, factor]) => (
              <FactorRow key={name} name={name} factor={factor} />
            ))}
          </div>
          {/* Top headlines */}
          {stock.top_headlines?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Recent Headlines
              </div>
              {stock.top_headlines.map((h, i) => (
                <a key={i} href={safeHref(h.link)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div style={{ fontSize: 11, color: 'var(--text)', marginBottom: 5, lineHeight: 1.4,
                    borderLeft: `2px solid ${ARTICLE_SENTIMENT_META[h.sentiment]?.color || 'var(--border)'}`,
                    paddingLeft: 8 }}>
                    {h.title}
                    <span style={{ color: 'var(--muted)', marginLeft: 6 }}>— {h.source}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
          {!stock.technical_available && (
            <div style={{ marginTop: 8, fontSize: 10, color: 'var(--muted)', fontStyle: 'italic' }}>
              ⚠️ Technical signals unavailable — connect Fyers for full multi-factor analysis
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sentiment column ──────────────────────────────────────────────────────────
function SentimentColumn({ title, stocks, compositeKey, activeStock, onSelect }) {
  const meta = COMPOSITE_META[compositeKey];
  return (
    <div className="card" style={{ padding: '14px', borderTop: `3px solid ${meta.color}` }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: meta.color, marginBottom: 12 }}>
        {meta.label}
        <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--muted)', marginLeft: 6 }}>
          ({stocks.length} stocks)
        </span>
      </div>
      {stocks.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>No signals in this category</div>
      ) : (
        stocks.slice(0, 10).map(s => (
          <StockSentimentCard
            key={s.symbol}
            stock={s}
            onClick={sym => onSelect(activeStock === sym ? null : sym)}
            active={activeStock === s.symbol}
          />
        ))
      )}
    </div>
  );
}

// ── News article card ─────────────────────────────────────────────────────────
function NewsCard({ item, activeStock, onStockClick }) {
  const meta = ARTICLE_SENTIMENT_META[item.sentiment] || ARTICLE_SENTIMENT_META.neutral;
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderLeft: `3px solid ${meta.color}`,
      borderRadius: 8, padding: '12px 14px', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
          background: item.source_color + '22', color: item.source_color,
          border: `1px solid ${item.source_color}44`,
        }}>
          {item.source.toUpperCase()}
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{item.published}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99,
          background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
        }}>
          {meta.label} {item.sentiment_score !== 0 ? `(${item.sentiment_score > 0 ? '+' : ''}${item.sentiment_score})` : ''}
        </span>
      </div>
      <a href={safeHref(item.link)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        <div
          style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.45, marginBottom: 6, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text)'}
        >
          {item.title}
        </div>
      </a>
      {item.description && (
        <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 8 }}>{item.description}</div>
      )}
      {item.stocks.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {item.stocks.map(s => (
            <span
              key={s.symbol}
              onClick={() => onStockClick(s.symbol)}
              style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 99, cursor: 'pointer', fontWeight: 600,
                background: activeStock === s.symbol ? 'var(--accent)' : 'var(--bg)',
                color:      activeStock === s.symbol ? '#fff' : 'var(--accent)',
                border:     `1px solid ${activeStock === s.symbol ? 'var(--accent)' : 'var(--accent)44'}`,
              }}
            >
              {s.symbol}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Trending sidebar ──────────────────────────────────────────────────────────
function TrendingPanel({ trending, activeStock, onSelect }) {
  if (!trending.length) return null;
  return (
    <div className="card" style={{ padding: '14px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>🔥 Trending</div>
      {trending.map(s => {
        const meta   = COMPOSITE_META[s.composite_label || s.label] || COMPOSITE_META.neutral;
        const active = activeStock === s.symbol;
        return (
          <div
            key={s.symbol}
            onClick={() => onSelect(active ? null : s.symbol)}
            style={{
              padding: '6px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 3,
              background: active ? meta.bg : 'transparent',
              border: active ? `1px solid ${meta.border}` : '1px solid transparent',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: active ? meta.color : 'var(--text)' }}>{s.symbol}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{s.name}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <span style={{ fontSize: 10, color: meta.color, fontWeight: 700 }}>{meta.short.split(' ')[0]}</span>
              <span style={{ fontSize: 10, background: 'var(--bg)', borderRadius: 99, padding: '1px 5px', color: 'var(--accent)', fontWeight: 700 }}>
                {s.mentions}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function NewsStocks() {
  const [items,         setItems]         = useState([]);
  const [total,         setTotal]         = useState(0);
  const [sentimentData, setSentimentData] = useState(null);
  const [trending,      setTrending]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [activeStock,   setActiveStock]   = useState(null);
  const [sentFilter,    setSentFilter]    = useState('');
  const [page,          setPage]          = useState(1);
  const [lastUpdated,   setLastUpdated]   = useState(null);
  const [activeTab,     setActiveTab]     = useState('overview');
  const intervalRef = useRef(null);

  const fetchAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const params = { page, page_size: 30 };
      if (activeStock) params.stock     = activeStock;
      if (sentFilter)  params.sentiment = sentFilter;

      const [newsRes, sentRes, trendRes] = await Promise.all([
        getNews(params),
        getNewsSentiment(),
        getTrendingStocks(12),
      ]);
      setItems(newsRes.data.items || []);
      setTotal(newsRes.data.total || 0);
      setSentimentData(sentRes.data);
      setTrending(trendRes.data.trending || []);
      setLastUpdated(new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' }));
    } catch { /* silent */ } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, activeStock, sentFilter]);

  useEffect(() => {
    setLoading(true);
    fetchAll();
    intervalRef.current = setInterval(() => fetchAll(true), 5 * 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [fetchAll]);

  const handleStockSelect = (sym) => {
    setActiveStock(sym);
    setPage(1);
    if (sym) setActiveTab('feed');
  };

  const sd    = sentimentData || {};
  const sBull = (sd.strong_bullish || []).length;
  const bull  = (sd.bullish        || []).length;
  const sBear = (sd.strong_bearish || []).length;
  const bear  = (sd.bearish        || []).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>📰 Market News &amp; Sentiment</h1>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span>{sd.total_articles ?? '—'} articles · {sd.total_stocks ?? '—'} stocks</span>
            {sBull > 0 && <span style={{ color: '#16a34a', fontWeight: 700 }}>🚀 {sBull} strong bull</span>}
            {bull  > 0 && <span style={{ color: 'var(--green)', fontWeight: 700 }}>▲ {bull} bullish</span>}
            {bear  > 0 && <span style={{ color: 'var(--red)',   fontWeight: 700 }}>▼ {bear} bearish</span>}
            {sBear > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}>💣 {sBear} strong bear</span>}
            {sd.technical_available === false && (
              <span style={{ color: 'var(--yellow)', fontWeight: 500 }}>⚠️ News-only (connect Fyers for technical confirmation)</span>
            )}
            {sd.technical_available && (
              <span style={{ color: 'var(--green)', fontWeight: 500 }}>✅ 5-factor analysis active</span>
            )}
            {lastUpdated && <span>· {lastUpdated}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(activeStock || sentFilter) && (
            <button className="btn btn-ghost" style={{ fontSize: 12 }}
              onClick={() => { setActiveStock(null); setSentFilter(''); setPage(1); }}>
              ✕ Clear
            </button>
          )}
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => fetchAll(true)} disabled={refreshing}>
            {refreshing ? '⟳ Refreshing…' : '⟳ Refresh'}
          </button>
        </div>
      </div>

      {/* Active filter pills */}
      {(activeStock || sentFilter) && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {activeStock && (
            <span style={{ fontSize: 12, padding: '3px 12px', borderRadius: 99, background: 'var(--accent)', color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              {activeStock} <span style={{ cursor: 'pointer' }} onClick={() => { setActiveStock(null); setPage(1); }}>✕</span>
            </span>
          )}
          {sentFilter && (
            <span style={{ fontSize: 12, padding: '3px 12px', borderRadius: 99, fontWeight: 600,
              background: ARTICLE_SENTIMENT_META[sentFilter]?.bg,
              color:      ARTICLE_SENTIMENT_META[sentFilter]?.color,
              border:     `1px solid ${ARTICLE_SENTIMENT_META[sentFilter]?.border}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {sentFilter} <span style={{ cursor: 'pointer' }} onClick={() => { setSentFilter(''); setPage(1); }}>✕</span>
            </span>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {[['overview', '🧭 Sentiment Overview'], ['feed', '📋 News Feed']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '8px 16px',
            fontSize: 13, fontWeight: 600, marginBottom: -1,
            color:        activeTab === key ? 'var(--accent)' : 'var(--muted)',
            borderBottom: activeTab === key ? '2px solid var(--accent)' : '2px solid transparent',
          }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'var(--muted)', padding: 24 }}>Loading news &amp; running sentiment analysis…</div>
      ) : activeTab === 'overview' ? (
        /* ── OVERVIEW TAB ── */
        <div>
          {/* How it works banner */}
          <div style={{
            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
            padding: '10px 16px', marginBottom: 16, fontSize: 12, color: 'var(--muted)',
            display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <span style={{ fontWeight: 700, color: 'var(--text)' }}>5-Factor Analysis:</span>
            {Object.entries(FACTOR_NAMES).map(([k, v]) => (
              <span key={k}>{FACTOR_ICONS[k]} {v}</span>
            ))}
            <span style={{ marginLeft: 'auto', fontStyle: 'italic' }}>
              Confidence = how many factors agree · Click any stock for factor breakdown
            </span>
          </div>

          {/* Strong signals row */}
          {(sBull > 0 || sBear > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <SentimentColumn title="Strong Bull" stocks={sd.strong_bullish || []}
                compositeKey="strong_bullish" activeStock={activeStock} onSelect={handleStockSelect} />
              <SentimentColumn title="Strong Bear" stocks={sd.strong_bearish || []}
                compositeKey="strong_bearish" activeStock={activeStock} onSelect={handleStockSelect} />
            </div>
          )}

          {/* Regular signals row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <SentimentColumn title="Bullish" stocks={sd.bullish || []}
              compositeKey="bullish" activeStock={activeStock} onSelect={handleStockSelect} />
            <SentimentColumn title="Bearish" stocks={sd.bearish || []}
              compositeKey="bearish" activeStock={activeStock} onSelect={handleStockSelect} />
          </div>

          {/* Neutral strip */}
          {(sd.neutral || []).length > 0 && (
            <div className="card" style={{ padding: '12px 16px' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
                ➡️ Neutral · mixed or insufficient signals
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(sd.neutral || []).slice(0, 25).map(s => (
                  <span
                    key={s.symbol}
                    onClick={() => handleStockSelect(activeStock === s.symbol ? null : s.symbol)}
                    style={{
                      fontSize: 12, padding: '3px 10px', borderRadius: 99, cursor: 'pointer', fontWeight: 600,
                      background: activeStock === s.symbol ? 'var(--accent)' : 'var(--bg)',
                      color:      activeStock === s.symbol ? '#fff' : 'var(--text)',
                      border:     '1px solid var(--border)',
                    }}
                  >
                    {s.symbol}
                    <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 4 }}>{s.mentions}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── FEED TAB ── */
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 16, alignItems: 'start' }}>
          <div>
            {/* Sentiment filter */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {['', 'bullish', 'bearish', 'neutral'].map(s => {
                const meta = s ? ARTICLE_SENTIMENT_META[s] : null;
                return (
                  <button key={s} className="btn btn-ghost"
                    style={{
                      fontSize: 12, padding: '4px 12px',
                      color:      sentFilter === s ? (meta?.color || 'var(--accent)') : 'var(--muted)',
                      fontWeight: sentFilter === s ? 700 : 400,
                    }}
                    onClick={() => { setSentFilter(s); setPage(1); }}
                  >
                    {s === '' ? 'All' : (meta?.label || s)}
                  </button>
                );
              })}
            </div>

            {items.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', color: 'var(--muted)', padding: 32 }}>
                {activeStock ? `No news found for ${activeStock}.` : 'No articles match the current filter.'}
              </div>
            ) : (
              <>
                {items.map((item, i) => (
                  <NewsCard key={`${item.pub_ts}-${i}`} item={item} activeStock={activeStock}
                    onStockClick={sym => handleStockSelect(activeStock === sym ? null : sym)} />
                ))}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 8 }}>
                  <button className="btn btn-ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  <span style={{ fontSize: 13, color: 'var(--muted)', padding: '6px 0' }}>Page {page} · {total}</span>
                  <button className="btn btn-ghost" disabled={items.length < 30} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              </>
            )}
          </div>
          <TrendingPanel trending={trending} activeStock={activeStock} onSelect={handleStockSelect} />
        </div>
      )}
    </div>
  );
}
